import React, { useMemo } from 'react'

import { compose } from 'ramda'
import { connect } from 'react-redux'

import { ReportMetric, ReportValue, Team } from '@driftt/api-types'

import { IQueryConfig } from 'api/report/query/types'
import * as EmbedSelectors from 'modules/embed/selectors'
import * as FilterSelectors from 'modules/filter/selectors'
import { ITimePeriod } from 'modules/filter/types'
import { mapDatesToSeriesLabels, TypeFormats } from 'utils/format'

interface IOwnProps {
  metric: string
}

interface IStateProps {
  timePeriod: ITimePeriod
  value?: ReportValue
  botStatus?: string
  teamSelected?: Team[]
  workspaceAgents?: any
  compoundQuery?: any
  agentsSelected?: { value: ReportValue }[]
  widgetStatus?: string
  inboxSelected?: number
  classificationId?: number
  metrics?: ReportMetric[]
  timezone?: string
}

const extendQueryWithFilters = (
  metric: string,
  props: IStateProps & IOwnProps,
  startDate: string,
  endDate: string
): IQueryConfig => {
  const {
    botStatus,
    widgetStatus,
    agentsSelected,
    inboxSelected,
    teamSelected,
    workspaceAgents,
    compoundQuery,
    classificationId,
    timezone,
  } = props

  return {
    metric,
    startDate,
    endDate,
    botStatus,
    widgetStatus,
    agentsSelected,
    inboxSelected,
    teamSelected,
    workspaceAgents,
    compoundQuery,
    classificationId,
    timezone,
  }
}

const mapStateToProps = (state): IStateProps => ({
  timePeriod: FilterSelectors.timePeriod(state),
  botStatus: FilterSelectors.botStatus(state),
  widgetStatus: FilterSelectors.widgetStatus(state),
  agentsSelected: FilterSelectors.agentsSelected(state),
  inboxSelected: FilterSelectors.inboxSelected(state),
  teamSelected: FilterSelectors.teamSelected(state),
  workspaceAgents: FilterSelectors.workspaceAgents(state),
  compoundQuery: FilterSelectors.compoundQuery(state),
  classificationId: FilterSelectors.selectedClassificationId(state),
  timezone: EmbedSelectors.timezoneSelector(state),
})

interface IQueryConfigFormattedProps {
  startDate: string
  endDate: string
  prevStartDate: string
  prevEndDate: string
  legendText: ReturnType<typeof mapDatesToSeriesLabels>
  currentSeriesLabel: string
  prevSeriesLabel: string
}

export interface IQueryConfigProps
  extends IStateProps,
    IOwnProps,
    IQueryConfigFormattedProps {
  queryConfig: IQueryConfig
  prevQueryConfig: IQueryConfig
}

const withQueryConfig = <P extends Record<string, unknown>>(
  Component: React.ComponentType<P>
) =>
  function WithQueryConfig(props: IStateProps & IOwnProps & P) {
    const { timePeriod, metric } = props

    const formattedProps = useMemo<IQueryConfigFormattedProps>(() => {
      const legendText = mapDatesToSeriesLabels(
        timePeriod.startDate,
        timePeriod.endDate,
        timePeriod.prevStart,
        timePeriod.prevEnd
      )
      return {
        startDate: TypeFormats.DATE(timePeriod.startDate),
        endDate: TypeFormats.DATE(timePeriod.endDate),
        prevStartDate: TypeFormats.DATE(timePeriod.prevStart),
        prevEndDate: TypeFormats.DATE(timePeriod.prevEnd),
        legendText,
        currentSeriesLabel: legendText.series,
        prevSeriesLabel: legendText.previousSeries,
      }
    }, [timePeriod])

    const queryConfig = useMemo(
      () =>
        extendQueryWithFilters(
          metric,
          props,
          formattedProps.startDate,
          formattedProps.endDate
        ),
      [props, formattedProps, metric]
    )

    const prevQueryConfig = useMemo(
      () =>
        extendQueryWithFilters(
          metric,
          props,
          formattedProps.prevStartDate,
          formattedProps.prevEndDate
        ),
      [props, formattedProps, metric]
    )

    return (
      <Component
        {...props}
        {...formattedProps}
        queryConfig={queryConfig}
        prevQueryConfig={prevQueryConfig}
      />
    )
  }

export default compose<any, any, any>(connect(mapStateToProps), withQueryConfig)
